import type { DashboardRole } from "@/types/dashboard";

export type ReportLabelKey =
  | "reportTitle"
  | "dashboardSectionTitle"
  | "overviewTitle"
  | "serviceAreaTitle"
  | "recommendationsTitle"
  | "recommendationSectionLabel"
  | "backToReports"
  | "openFullPage"
  | "editReport"
  | "downloadPdf"
  | "downloadHtml"
  | "print"
  | "generatedReport"
  | "preparedBy"
  | "generatedBy"
  | "reportingPeriod"
  | "scope"
  | "heroBadge"
  | "heroTitle"
  | "heroCopy"
  | "heroChipHtmlPreview"
  | "heroChipPdfTemplate"
  | "snapshotLeadCopy"
  | "metricTotalTickets"
  | "metricOngoing"
  | "metricClosed"
  | "metricOpen"
  | "metricTicketRequests"
  | "metricNewLeads"
  | "metricCompletionRate"
  | "metricOverdue";

export type ReportLabels = Record<ReportLabelKey, string | null>;

const INTERNAL_LABELS: ReportLabels = {
  reportTitle: "Monthly Progress Report",
  dashboardSectionTitle: "Executive Snapshot",
  overviewTitle: "Executive Snapshot",
  serviceAreaTitle: "Service Areas Covered",
  recommendationsTitle: "Overall Monthly Recommendation",
  recommendationSectionLabel: "Recommendation",
  backToReports: "Back to Reports",
  openFullPage: "Open Full Page",
  editReport: "Edit Report",
  downloadPdf: "Download PDF",
  downloadHtml: "Download HTML",
  print: "Print",
  generatedReport: "Generated Report",
  preparedBy: "Prepared By",
  generatedBy: "Generated By",
  reportingPeriod: "Reporting Period",
  scope: "Scope",
  heroBadge: "Proweaver Analytics",
  heroTitle: "A clean, internal report view with the same data used for PDF export.",
  heroCopy:
    "This report is designed as a website-style preview first, then exported from the same HTML template for a consistent PDF.",
  heroChipHtmlPreview: "HTML Preview First",
  heroChipPdfTemplate: "PDF From Same Template",
  snapshotLeadCopy: "KPI totals for task flow, ticket requests, and lead intake.",
  metricTotalTickets: "Total Tasks",
  metricOngoing: "In Progress",
  metricClosed: "Completed",
  metricOpen: "Pending",
  metricTicketRequests: "Ticket Requests",
  metricNewLeads: "New Leads",
  metricCompletionRate: "Completion Rate",
  metricOverdue: "Overdue",
};

const CLIENT_LABELS: ReportLabels = {
  ...INTERNAL_LABELS,
  overviewTitle: "Account Overview",
  serviceAreaTitle: "Services Covered",
  recommendationsTitle: "Recommendations",
  recommendationSectionLabel: "Recommendations",
  generatedBy: null,
  openFullPage: "Open Full Page",
  heroBadge: "Client Report",
  heroTitle: "A clean, client-facing report view with the same data used for PDF export.",
  snapshotLeadCopy: "KPI totals for ticket flow and lead intake.",
  metricTotalTickets: "Total Tickets",
  metricOngoing: "On-going",
  metricClosed: "Closed",
  metricOpen: "Open Ticket",
  metricTicketRequests: "Ticket Requests",
  metricNewLeads: "New Leads",
  metricCompletionRate: "Completion Rate",
  metricOverdue: "Overdue",
};

export function isClientRole(role?: DashboardRole | string | null): boolean {
  return String(role || "").toLowerCase() === "client";
}

export function getReportLabelByRole(
  role: DashboardRole | string | null | undefined,
  key: ReportLabelKey,
): string {
  const labels = isClientRole(role) ? CLIENT_LABELS : INTERNAL_LABELS;
  return labels[key] ?? INTERNAL_LABELS[key] ?? key;
}

export function getReportLabelsByRole(
  role: DashboardRole | string | null | undefined,
): ReportLabels {
  return isClientRole(role) ? CLIENT_LABELS : INTERNAL_LABELS;
}
